<template>
  <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
  <div class="relative z-50 lg:hidden" role="dialog" aria-modal="true">
    <!--
      Off-canvas menu backdrop, show/hide based on off-canvas menu state.
    -->
    <Transition
        enter-active-class="transition-opacity ease-linear duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity ease-linear duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
      <div v-if="showMenu" class="fixed inset-0 bg-surface-900/80"></div>
    </Transition>

    <div :class="[showMenu ? '' : 'pointer-events-none', 'fixed inset-0 flex']">

      <!--
        Off-canvas menu, show/hide based on off-canvas menu state.
      -->
      <Transition
          enter-active-class="transition ease-in-out duration-300 transform"
          enter-from-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition ease-in-out duration-300 transform"
          leave-from-class="translate-x-0"
          leave-to-class="-translate-x-full"
      >
        <div class="relative mr-16 flex w-full max-w-xs flex-1" v-if="showMenu">

          <!--
            Close button, show/hide based on off-canvas menu state.
          -->
          <Transition
              enter-active-class="ease-in-out duration-300"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="ease-in-out duration-300"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
          >
            <div v-if="showMenu" class="absolute left-full top-0 flex w-16 justify-center pt-5">
              <button @click="showMenu = false" type="button" class="-m-2.5 p-2.5">
                <span class="sr-only">Close sidebar</span>
                <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
                     aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </Transition>

          <!-- Sidebar component, swap this element with another sidebar if you like -->
          <div class="flex grow flex-col bg-surface-0 dark:bg-surface-800 pb-2">
            <div class="ribbon-1 left orange" v-if="isInternalEnv">{{ $t('components.sidenav.INTERNAL') }}</div>
            <div class="sticky top-0 z-10 px-4 mt-1">
              <div class="flex items-center h-16">
                <!-- Left spacer keeps the logo perfectly centered -->
                <div class="w-20" v-if="isInternalEnv"></div>

                <!-- Centered Logo -->
                <div class="flex-1 flex justify-center">
                  <img
                    class="h-10 w-auto"
                    src="/images/logos/haldex.png"
                    alt="Haldex"
                  >
                </div>

                <!-- Right-aligned Language Selector -->
                <div class="relative w-20 flex justify-end" v-if="isInternalOrStaging">
                  <button
                    @click="showLanguageDropdown = !showLanguageDropdown"
                    class="flex items-center space-x-1.5 p-1.5 text-xs border border-surface-200 dark:border-surface-600 rounded-md bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 hover:border-primary-400 transition-colors"
                  >
                    <!-- was: {{ getLanguageFlag(currentLocale) }} -->
                    <span :class="['fi', `fi-${flagCountry(currentLocale)}`]" class="mr-1"></span>
                    <span class="font-medium">{{ normalizedCurrentLocale.toUpperCase() }}</span>
                    <svg class="w-3 h-3 transition-transform duration-200" :class="{ 'rotate-180': showLanguageDropdown }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <!-- Dropdown Menu -->
                  <Transition
                    enter-active-class="transition ease-out duration-200"
                    enter-from-class="opacity-0 translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-1"
                  >
                    <div 
                      v-if="showLanguageDropdown"
                      class="absolute z-50 right-0 w-56 mt-9 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    >
                      <div class="py-1">
                        <button
                          v-for="(label, code) in availableLanguages"
                          :key="code"
                          @click="selectLanguage(code)"
                          class="w-full flex items-center space-x-3 px-3 py-2 text-sm text-surface-900 dark:text-surface-100 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors text-left"
                          :class="{ 'bg-primary-50 dark:bg-primary-900 text-primary-600 dark:text-primary-400': currentLocale === code }"
                        >
                          <!-- was: {{ getLanguageFlag(code) }} -->
                          <span :class="['fi', `fi-${flagCountry(code)}`]" class="mr-2"></span>
                          <span class="flex-1">{{ label }}</span>
                          <svg 
                            v-if="currentLocale === code"
                            class="w-4 h-4 text-primary-600 dark:text-primary-400" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Transition>

                  <!-- Click outside to close -->
                  <div 
                    v-if="showLanguageDropdown" 
                    @click="showLanguageDropdown = false"
                    class="fixed inset-0 z-40"
                  ></div>
                </div>
              </div>
              <Divider />
            </div>

            <div class="overflow-y-auto overflow-x-hidden px-6 py-3">
              <nav class="flex flex-1 flex-col">
                <ul role="list" class="flex flex-1 flex-col gap-y-7">

                  <template v-for="section in allRoutes">
                    <li v-if="section.children?.length">

                      <div v-if="section.title" class="text-xs font-semibold leading-6 text-surface-400">
                        {{ section.title }}
                      </div>

                      <ul role="list" class="-mx-2 space-y-1">
                        <li v-for="route in section.children">

                          <!-- Vue routes -->
                          <router-link v-if="route.to" :to="route.to" v-slot="{ isActive }">
                    <span
                        :class="[
                      isActive ? 'bg-surface-50 text-primary-600' : 'text-surface-700 hover:bg-surface-50 hover:dark:bg-surface-700',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                  ]">
                    <div class="relative flex flex-row">
                        <component :is="route.icon" :stroke-width="1.5"
                                   :class="[isActive ? 'text-primary-600' : 'text-surface-400 group-hover:text-surface-600', 'h-6 w-6 shrink-0']"></component>
                        <IconSettingsFilled v-if="route?.admin ?? false" class="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 shrink-0
                          bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white from-40% via-transparent to-transparent "
                                            :class="[isActive ? ' opacity-100 text-primary-600' : ' text-primary-500 ']">
                        </IconSettingsFilled>
                    </div>
                    {{ route.name }}
                  </span>
                          </router-link>

                          <!-- External links -->
                          <a v-else-if="route.url" :href="route.url" :target="route.target">
                    <span class="text-surface-700 dark:text-surface-400 hover:text-surface-600 hover:bg-surface-50 hover:dark:text-surface-100 hover:dark:bg-surface-800 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold">
                      <Component
                          :is="route.icon"
                          :stroke-width="1.25"
                          class="text-surface-400 dark:text-surface-600 group-hover:text-surface-600 dark:group-hover:text-surface-300 h-6 w-6 shrink-0"
                      />
                      {{ route.name }}
                    </span>
                          </a>

                          <!-- show object -->
                          <span v-else>{{ route }}</span>
                        </li>
                      </ul>
                    </li>
                  </template>

                </ul>
              </nav>
            </div>
          </div>

        </div>
      </Transition>
    </div>
  </div>

  <!-- Static sidebar for desktop -->
  <div class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
    <div class="ribbon-1 left orange" v-if="isInternalEnv">{{ $t('components.sidenav.INTERNAL') }}</div>
    <div class="global-border orange" v-if="isInternalEnv"></div>
    <!-- Sidebar component, swap this element with another sidebar if you like -->
    <div class="flex grow flex-col overflow-y-auto border-r border-surface-200 bg-surface-0 dark:bg-surface-900 dark:border-surface-700 shadow-xl">
      <!-- App Logo with Centered Logo and Right-aligned Language Selector -->
      <div class="sticky top-0 flex flex-col h-20 shrink-0 items-center justify-start mt-4 z-10 px-4">
        <div class="relative flex items-center w-full">
          <!-- Spacer for left side to balance the language selector on the right -->
          <div class="w-20" v-if="isInternalOrStaging"></div>

          <!-- Centered Logo -->
          <div class="flex-1 flex justify-center">
            <router-link :to="{ name:'landing' }" class="flex-shrink-0">
              <img class="h-14 w-auto"
                  src="/images/logos/haldex.png"
                  alt="Haldex">
            </router-link>
          </div>
          
          <!-- Right-aligned Language Selector -->
          <div class="relative w-20 flex justify-end" v-if="isInternalOrStaging">
            <button
              @click="showLanguageDropdown = !showLanguageDropdown"
              class="flex items-center space-x-1.5 p-1.5 text-xs border border-surface-200 dark:border-surface-600 rounded-md bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 hover:border-primary-400 transition-colors"
            >
              <!-- was: {{ getLanguageFlag(currentLocale) }} -->
              <span :class="['fi', `fi-${flagCountry(currentLocale)}`]" class="mr-1"></span>
              <span class="font-medium">{{ normalizedCurrentLocale.toUpperCase() }}</span>
              <svg 
                class="w-3 h-3 transition-transform duration-200"
                :class="{ 'rotate-180': showLanguageDropdown }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <Transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-1"
            >
              <div 
                v-if="showLanguageDropdown"
                class="absolute z-50 right-0 w-56 mt-9 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
              >
                <div class="py-1">
                  <button
                    v-for="(label, code) in availableLanguages"
                    :key="code"
                    @click="selectLanguage(code)"
                    class="w-full flex items-center space-x-3 px-3 py-2 text-sm text-surface-900 dark:text-surface-100 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors text-left"
                    :class="{ 'bg-primary-50 dark:bg-primary-900 text-primary-600 dark:text-primary-400': currentLocale === code }"
                  >
                    <span :class="['fi', `fi-${flagCountry(code)}`]" class="mr-2"></span>
                    <span class="flex-1">{{ label }}</span>
                    <svg 
                      v-if="currentLocale === code"
                      class="w-4 h-4 text-primary-600 dark:text-primary-400" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </Transition>

            <!-- Click outside to close -->
            <div 
              v-if="showLanguageDropdown" 
              @click="showLanguageDropdown = false"
              class="fixed inset-0 z-40"
            ></div>
          </div>
        </div>
        <Divider />
      </div>

      <div class="overflow-y-auto overflow-x-hidden mt-3 pt-3 pb-3 px-6 flex-1 flex flex-col">
        <nav class="flex flex-1 flex-col">
          <ul role="list" class="flex flex-1 flex-col gap-y-7">

            <template v-for="section in allRoutes">
              <li v-if="section.children?.length">

                <div v-if="section.title" class="text-xs font-semibold leading-6 text-surface-400">
                  {{ section.title }}
                </div>

                <ul role="list" class="-mx-2 space-y-1">
                  <li v-for="route in section.children">

                    <!-- Vue routes -->
                    <router-link v-if="route.to" :to="route.to" v-slot="{ isActive }">
                      <span
                        :data-cy="`${route.name}`"
                        :class="[
                        isActive ? 'bg-surface-50 text-primary-600' : 'text-surface-700 hover:bg-surface-50 hover:dark:bg-surface-700',
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                    ]">
                      <div class="relative flex flex-row">
                          <component :is="route.icon" :stroke-width="1.5"
                                     :class="[isActive ? 'text-primary-600' : 'text-surface-400 group-hover:text-surface-600', 'h-6 w-6 shrink-0']"></component>
                          <IconSettingsFilled v-if="route?.admin ?? false" class="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 shrink-0
                            bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white from-40% via-transparent to-transparent "
                                              :class="[isActive ? ' opacity-100 text-primary-600' : ' text-primary-500 ']">
                          </IconSettingsFilled>
                      </div>
                      {{ route.name }}
                    </span>
                    </router-link>

                    <!-- External links -->
                    <a v-else-if="route.url" :href="route.url" :target="route.target">
                      <span class="text-surface-700 dark:text-surface-400 hover:text-surface-600 hover:bg-surface-50 hover:dark:text-surface-100 hover:dark:bg-surface-800 group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold">
                        <Component
                            :is="route.icon"
                            :stroke-width="1.25"
                            class="text-surface-400 dark:text-surface-600 group-hover:text-surface-600 dark:group-hover:text-surface-300 h-6 w-6 shrink-0"
                        />
                        {{ route.name }}
                      </span>
                    </a>

                    <!-- show object -->
                     <!-- Don't dump the route object if we don't have a URL i.e. it's not setup properly -->
                    <!-- <span v-else>{{ route }}</span> -->
                  </li>
                </ul>
              </li>
            </template>

          </ul>
        </nav>
      </div>

      <!-- User Account Button -->
      <div class="border-t border-surface-100 inline-block text-left bg-surface-0 dark:bg-surface-900 dark:border-surface-800">
        <Button
            text
            severity="secondary"
            @click="toggle"
            class="w-full flex"
            data-cy="user-profile-toggle"
            >
          <div class="flex flex-row w-full items-center gap-x-4 px-3 py-3">
            <Avatar :label="authStore.getInitials" size="large" shape="circle"/>
            <div
                class="flex items-center justify-start text-sm font-semibold leading-6 text-surface-900 hover:bg-surface-50 dark:text-surface-100 dark:hover:bg-surface-800">
              <span class="sr-only">Your profile</span>
              <span aria-hidden="true">{{ authStore.getUsername }}</span>
            </div>
          </div>
        </Button>
        <Menu
            ref="menu"
            :model="accountRoutes"
            :popup="true"
            :pt="{
                    root: {
                      style: { left: '12px !important' }
                    },
                  }"
        >
          <template #start>
            <div class="px-5 py-3 border-b border-surface-100 dark:border-surface-600 mb-1">
              <p class="text-sm">{{ $t('components.sidenav.signed_in_as') }}</p>
              <p class="truncate text-sm font-medium text-surface-900 dark:text-surface-50">
                {{ authStore.getEmail }}
              </p>
            </div>
          </template>

          <template #item="{ item, props }">
            <a v-ripple class="flex align-items-center" v-bind="props.action" v-bind:data-cy="item.datacy || null">
              <span :class="item.icon" />
              <span class="ml-2">{{ item.label }}</span>
              <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
              <span
                v-if="item.shortcut"
                class="ml-auto border-1 surface-border border-round surface-100 text-xs p-1"
              >
                {{ item.shortcut }}
              </span>
            </a>
          </template>
        </Menu>
        <div class="px-4 pb-4 pt-4 flex flex-col items-start gap-1 border-t border-surface-100 bg-white">
          <span class="text-xs text-surface-400 leading-0 tracking-0 font-medium">SUMS {{ $t('components.sidenav.version') }} {{appVersion}}</span>
          <span class="text-xs text-surface-400 leading-0 tracking-0 font-mono">{{ appCommit }}</span>
          <span class="text-xs text-surface-400 leading-0 tracking-0">© {{ currentYear }} Haldex™</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Top navigation bar for smaller screens -->
  <div class="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
    
    <div class="global-border orange shorter"></div>
    <button @click="showMenu = true;" type="button" class="-m-2.5 p-2.5 text-surface-700 lg:hidden">
      <span class="sr-only">Open sidebar</span>
      <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/>
      </svg>
    </button>
    <div class="flex-1 text-sm font-semibold leading-6 text-surface-900 capitalize">{{ $route.name }}</div>
    <a href="#" @click="toggle">
      <span class="sr-only">Your profile</span>
      <Avatar :label="authStore.getInitials" shape="circle" />
    </a>
  </div>

  <!-- User Account Dialog -->
  <UserAccountDialog ref="userAccountDialog"/>
  <!-- Hello World Dialog -->
  <TwoFactorAuthDialog ref="twoFactorAuthDialog" />
  <!-- <div style="position:fixed; background:yellow; z-index:9999">
    {{ authStore.user.two_factor_enabled }}
  </div> -->
</template>

<script>
import {useAuthStore} from "@/stores/auth.js";
import {mapStores} from "pinia";
import axios from 'axios';

import {
  IconApi,
  IconAward,
  IconBook,
  IconBuildingCommunity,
  IconBuildingFactory2,
  IconCertificate2,
  IconChevronRight,
  IconClipboardCheck,
  IconCode,
  IconComponents,
  IconDeviceLaptop,
  IconHeadset,
  IconHome,
  IconListSearch,
  IconReport,
  IconRouter,
  IconSettings,
  IconSettingsFilled,
  IconShieldCog,
  IconTruck,
  IconUpload,
  IconUsers,
  IconZoomCode,
  IconClipboardList,
  IconBulb,
} from "@tabler/icons-vue";
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/vue'
import UserAccountDialog from "@/Dialogs/UserAccountDialog.vue";
import TwoFactorAuthDialog from "@/Dialogs/TwoFactorAuthDialog.vue";

const normalizeLocale = (locale) => {
  if (typeof locale !== 'string' || locale.length === 0) {
    return 'en';
  }

  return locale.toLowerCase().split('-')[0].split('_')[0];
};


export default {
  name: "Sidenav",
  components: {
    UserAccountDialog,
    TwoFactorAuthDialog,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    IconHome,
    IconReport,
    IconRouter,
    IconShieldCog,
    IconBuildingFactory2,
    IconTruck,
    IconComponents,
    IconChevronRight,
    IconApi,
    IconClipboardCheck,
    IconDeviceLaptop,
    IconListSearch,
    IconCode,
    IconSettings,
    IconSettingsFilled,
    IconBuildingCommunity,
    IconZoomCode,
    IconCertificate2,
    IconUsers,
    IconUpload,
    IconClipboardList,
  },
  data() {
    return {
      showMenu: false,
      isActive: false,
      showLanguageDropdown: false,
      showMobileLanguageDropdown: false,
      appVersion: APPLICATION_VERSION,
      appCommit: APPLICATION_COMMIT,
      appEnv: import.meta.env.VITE_APP_ENV ?? 'production',
      currentLocale: normalizeLocale(this.$i18n.locale),
      availableLanguages: {
        en: 'English',
        de: 'Deutsch',
        fr: 'Français', 
        es: 'Español',
        pl: 'Polski',
        nl: 'Nederlands',
        it: 'Italiano',
        fi: 'Suomi',
        ru: 'Русский',
        cs: 'Čeština',
        hu: 'Magyar',
        ro: 'Română',
        sv: 'Svenska',
        tr: 'Türkçe',
      },
      translationText: '',
      translatedResult: '',
      showTranslationDialog: false,
      isTranslating: false,
      localeToCountry: {
        en: 'gb',  
        de: 'de',
        fr: 'fr',
        es: 'es',
        pl: 'pl',
        nl: 'nl',
        it: 'it',
        fi: 'fi',
        ru: 'ru',
        cs: 'cz',
        hu: 'hu',
        ro: 'ro',
        sv: 'se',
        tr: 'tr',
      },
    }
  },
  
  computed: {
    ...mapStores(useAuthStore),
    isInternalEnv() {
      return this.appEnv === 'internal';
    },
    isStagingEnv() {
      return this.appEnv === 'staging';
    },
    isInternalOrStaging() {
      return this.isInternalEnv || this.isStagingEnv;
    },
    normalizedCurrentLocale() {
      return this.supportedLocale(this.currentLocale);
    },
    currentYear() {
      return new Date().getFullYear();
    },
    allRoutes() {
      return [
        this.mainRoutesSection,
        this.adminRoutesSection,
        this.internalRoutesSection,
        this.supportRoutesSection,
      ];
    },
    accountRoutes() {
      return [
        {
          label: this.$t('components.sidenav.account_settings'),
          command: this.showUserAccountDialog,
          datacy: 'account-settings',
        },
        // {
        //     label: this.authStore.user.two_factor_enabled ? this.$t('components.sidenav.show_2fa') : this.$t('components.sidenav.enable_2fa'),
        //     command: this.startTwoFactorAuth,
        //     datacy: 'twofa-toggle',
        // },
        // ...(this.authStore.user.two_factor_enabled
        // ? [{
        //     label: this.$t('components.sidenav.disable_2fa'),
        //     command: this.disableTwoFactorAuth,
        //     datacy: 'disable-twofa',
        //   }]
        // : []),
        {
          label: this.$t('components.sidenav.sign_out'),
          command: this.logoutUser,
          datacy: 'logout',
        }
      ]
    },

    mainRoutesSection() {
      return {
        children: [
          {
            name: this.$t('components.sidenav.home'),
            to: {name: 'home'},
            icon: IconHome,
          },
          {
            name: this.$t('components.sidenav.diag_plus_plus'),
            to: {name: 'diag-plus-plus'},
            permission: 'download:diag-plus-plus',
            icon: IconDeviceLaptop,
          },
          {
            name: this.$t('components.sidenav.assets'),
            to: {name: 'assets'},
            permission: 'read:assets',
            icon: IconTruck,
          },
          {
            name: this.$t('components.sidenav.devices'),
            to: {name: 'devices'},
            permission: 'read:devices',
            icon: IconRouter,
          },
          {
            name: this.$t('components.sidenav.reports'),
            to: {name: 'reports'},
            permission: 'read:reports',
            icon: IconReport,
          },
          {
            name: this.$t('components.sidenav.manufacturers'),
            to: {name: 'manufacturers'},
            permission: 'create:device-manufacturers|update:device-manufacturers',
            icon: IconBuildingFactory2,
            admin: false
          },
          {
            name: this.$t('components.sidenav.rx_swin_search'),
            to: {name: 'rxswin.search'},
            permission: 'read:rx-swins',
            icon: IconListSearch,
            admin: false
          },
        ].filter(this.shouldShowRoute)
      };
    },
    internalRoutesSection() {
      return {
        title: this.$t('components.sidenav.internal'),
        children: [
          {
            name: this.$t('components.sidenav.device_import'),
            to: {name: 'admin.devices.import'},
            permission: 'create:imports',
            icon: IconUpload,
          },
          {
            name: this.$t('components.sidenav.firmware_lookup'),
            to: {name: 'firmware'},
            permission: 'firmwares:lookup',
            icon: IconZoomCode,
            admin: false
          },
        ].filter(this.shouldShowRoute)
      }
    },
    adminRoutesSection() {
      return {
        title: this.$t('components.sidenav.administration'),
        children: [
          {
            name: this.$t('components.sidenav.assets'),
            to: {name: 'admin.assets'},
            permission: 'create:assets|update:assets',
            icon: IconTruck,
            admin: true
          },
          {
            name: this.$t('components.sidenav.companies'),
            to: {name: 'admin.companies'},
            permission: 'create:companies|update:companies',
            icon: IconBuildingCommunity,
            admin: true
          },
          {
            name: this.$t('components.sidenav.devices'),
            to: {name: 'admin.devices'},
            permission: 'create:devices|update:devices'
                + '|create:device-models|update:device-models'
                + '|create:device-revisions|update:device-revisions',
            icon: IconRouter,
            admin: true
          },
          {
            name: this.$t('components.sidenav.firmware'),
            to: {name: 'admin.firmware'},
            permission: 'create:firmwares|update:firmwares'
                + '|create:firmware-types|update:firmware-types',
            icon: IconCode,
            admin: true
          },
          {
            name: this.$t('components.sidenav.regulations'),
            to: {name: 'admin.regulations'},
            permission: 'create:regulations|update:regulations',
            icon: IconCertificate2,
            admin: true
          },
          {
            name: this.$t('components.sidenav.users'),
            to: {name: 'admin.users'},
            permission: 'create:users|update:users'
                + '|create:roles|update:roles'
                + '|create:permissions|update:permissions',
            icon: IconUsers,
            admin: true
          },
          {
            name: 'Config Requests',
            to: {name: 'admin.config-requests'},
            permission: 'read:config-change-request',
            icon: IconClipboardCheck,
            admin: true
          },
          {
            name: 'API Logs',
            to: {name: 'admin.api-logs'},
            permission: 'read:api-logs',
            icon: IconApi,
            admin: true
          }
        ].filter(this.shouldShowRoute),
      };
    },
    supportRoutesSection() {
      return {
        title: this.$t('components.sidenav.support'),
        children: [
          {
            name: this.$t('components.sidenav.r156_information'),
            to: {name: 'r156-information'},
            icon: IconAward,
          },
          {
            name: this.$t('components.sidenav.user_guide'),
            url: import.meta.env.VITE_USER_GUIDE_URL,
            target: "_blank",
            icon: IconBook,
          },
          {
            name: this.$t('components.sidenav.service_desk'),
            url: import.meta.env.VITE_CUSTOMER_SUPPORT_URL,
            target: "_blank",
            icon: IconHeadset,
          },
          {
            name: this.$t('components.sidenav.trailer_guide'),
            url: import.meta.env.VITE_TRAILER_GUIDE_URL,
            target: "_blank",
            icon: IconClipboardList,
          },
          {
            name: this.$t('components.sidenav.saf_iq_portal'),
            url: import.meta.env.VITE_SAF_IQ_PORTAL_URL,
            target: "_blank",
            icon: IconBulb,
          },
        ],
      };
    },
  },
  methods: {
    supportedLocale(locale) {
      const normalizedLocale = normalizeLocale(locale);
      return this.availableLanguages[normalizedLocale] ? normalizedLocale : 'en';
    },
    changeLanguage() {
      this.currentLocale = this.supportedLocale(this.currentLocale);

      // Change the i18n locale
      this.$i18n.locale = this.currentLocale;
      
      // Store the preference in localStorage
      localStorage.setItem('user-locale', this.currentLocale);
      document.documentElement.setAttribute('lang', this.currentLocale);
      axios.defaults.headers.common['Accept-Language'] = this.currentLocale;
      this.$emit('language-changed', this.currentLocale);
      this.saveLanguagePreference();
      this.$toast.add({
        severity: 'success',
        summary: this.$t('components.sidenav.language_changed'),
        detail: `${this.$t('components.sidenav.language_changed_alert')} ${this.availableLanguages[this.currentLocale]}`,
        life: 2000
      });
    },
    
    async saveLanguagePreference() {
      try {
        await axios.post('/user/language-preference', {
          locale: this.currentLocale
        });
        console.log('Language preference saved successfully');
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    },
    
    async loadLanguagePreference() {
      try {
        if (this.authStore.isAuthenticated) {
          const response = await axios.get('/user/language-preference');
          const backendLocale = this.supportedLocale(response.data.language_preference);
          if (this.availableLanguages[backendLocale]) {
            this.currentLocale = backendLocale;
            this.$i18n.locale = backendLocale;
            localStorage.setItem('user-locale', backendLocale);
            document.documentElement.setAttribute('lang', backendLocale);
            axios.defaults.headers.common['Accept-Language'] = backendLocale;
            return;
          }
        }
        
        // Fallback to localStorage
        const savedLocale = this.supportedLocale(localStorage.getItem('user-locale'));
        if (this.availableLanguages[savedLocale]) {
          this.currentLocale = savedLocale;
          this.$i18n.locale = savedLocale;
          document.documentElement.setAttribute('lang', savedLocale);
          axios.defaults.headers.common['Accept-Language'] = savedLocale;
        }
      } catch (error) {
        console.error('Failed to load language preference:', error);
        const savedLocale = this.supportedLocale(localStorage.getItem('user-locale'));
        if (this.availableLanguages[savedLocale]) {
          this.currentLocale = savedLocale;
          this.$i18n.locale = savedLocale;
          document.documentElement.setAttribute('lang', savedLocale);
          axios.defaults.headers.common['Accept-Language'] = savedLocale;
        }
      }
    },
    getLanguageFlag(code) {
      return this.languageFlags[code] || '🌐';
    },
    
    selectLanguage(code) {
      this.currentLocale = this.supportedLocale(code);
      this.showLanguageDropdown = false;
      this.changeLanguage();
    },
    logoutUser() {
      const saved = localStorage.getItem('user-locale'); // preserve across logout
      this.authStore.logoutUser()
      .finally(() => {
        if (saved) localStorage.setItem('user-locale', saved);
        this.$router.go(0);
      });
    },
    shouldShowRoute(route) {
      if (route.hideInProduction && this.appEnv === 'production') {
        return false;
      }
      return route.permission === undefined || this.$auth.hasPermission(route.permission);
    },
    toggle($event) {
      this.$refs.menu.toggle($event);
    },
    showUserAccountDialog() {
      this.$refs.userAccountDialog.open();
    },
    startTwoFactorAuth() {
      this.$refs.twoFactorAuthDialog.open();
      this.$refs.twoFactorAuthDialog.step=1;
      // this.modelOpen = true; // Show the modal when this method is called
    },
    disableTwoFactorAuth() {
      this.$refs.twoFactorAuthDialog.open();
      this.$refs.twoFactorAuthDialog.step=5;
      // this.modelOpen = true; // Show the modal when this method is called
    },
    flagCountry(code) {
      return this.localeToCountry[this.supportedLocale(code)] || 'un';
    },
  },
  
  mounted() {
    this.currentLocale = this.supportedLocale(this.currentLocale);
    this.$i18n.locale = this.currentLocale;
    document.documentElement.setAttribute('lang', this.currentLocale);
    axios.defaults.headers.common['Accept-Language'] = this.currentLocale;

    // Load language preference when component mounts
    this.loadLanguagePreference();
  }
}
</script>

<style scoped>
@import 'flag-icons/css/flag-icons.min.css';
</style>
